import { negotiationStatusCodeTransformer } from "@/lib/formatters/NegotiationStatusCodeTransformer";
import { formatThousandsCheckNull } from "@/lib/formatters/numbers";
import {
  currencyValue,
  percentLabelFormatterDecimalValue,
} from "@/features/statistics/lib/statisticsUtil";
import { Indent, Txt } from "@stenajs-webui/core";
import { Link } from "@stenajs-webui/elements";
import {
  StandardTableConfigWithNoGroups,
  createColumnConfig,
} from "@stenajs-webui/grid";

import { stripLineFromSellerName } from "@/lib/transformers/SellerNameTransformer";
import { CustomerStatisticsListItem } from "../types/customer.types";

export interface StatisticsCustomerDataListItem {
  customerName?: string | null;
  negotiationStatus?: string | null;
  customerNumber: number;
  statNumber: number | null;
  parentNumber: number | null;
  sellerId: string | null;
  averageTarget?: number | null;
  averageNegotiationRate?: number | null;
  avgMeterPrice?: number | null;
  avgMeterPriceDiffInPercent?: number | null;
  lastYearVolume?: number;
  expectedVolume?: number | null;
}

export type StatisticsCustomerDataListColumns =
  | "customerName"
  | "negotiationStatus"
  | "customerNumber"
  | "statNumber"
  | "parentNumber"
  | "sellerId"
  | "averageTarget"
  | "averageNegotiationRate"
  | "avgMeterPrice"
  | "avgMeterPriceDiffInPercent"
  | "lastYearVolume"
  | "expectedVolume";

export const createByCustomerTableConfig = (
  currencyCode: string,
  onClickCustomer: (customerId: string) => void
): StandardTableConfigWithNoGroups<
  CustomerStatisticsListItem,
  StatisticsCustomerDataListColumns
> => ({
  keyResolver: (item) => item.customerNumber.toString(),
  disableSorting: false,
  initialSortOrder: "customerName",
  stickyHeader: true,
  rowIndent: 2,
  columns: {
    customerName: createColumnConfig((item) => item.customerName, {
      columnLabel: "Name",
      renderCell: ({ label, item }) => (
        <Indent>
          <Txt whiteSpace={"nowrap"}>
            <Link onClick={() => onClickCustomer(String(item.customerNumber))}>
              {label}
            </Link>
          </Txt>
        </Indent>
      ),
    }),
    negotiationStatus: createColumnConfig(
      (item) => negotiationStatusCodeTransformer(item.negotiationStatus ?? ""),
      {
        columnLabel: "Negotiation status",
      }
    ),
    customerNumber: createColumnConfig((item) => item.customerNumber, {
      columnLabel: "Customer no.",
    }),
    statNumber: createColumnConfig((item) => item.statNumber, {
      columnLabel: "Stat no.",
    }),
    parentNumber: createColumnConfig((item) => item.parentNumber, {
      columnLabel: "Parent no.",
    }),
    sellerId: createColumnConfig(
      (item) => stripLineFromSellerName(item.sellerId ?? "").toUpperCase(),
      {
        columnLabel: "Seller",
      }
    ),
    lastYearVolume: createColumnConfig(
      (item) => item.volumeAndTargetForSelected.lastYearVolume,
      {
        columnLabel: "12 mon. volume",
        itemLabelFormatter: formatThousandsCheckNull,
      }
    ),
    expectedVolume: createColumnConfig(
      (item) => item.volumeAndTargetForSelected.expectedVolume,
      {
        columnLabel: "Expected volume",
        itemLabelFormatter: formatThousandsCheckNull,
      }
    ),
    averageTarget: {
      itemValueResolver: (item) =>
        item.volumeAndTargetForSelected.averageTarget,
      columnLabel: "Target rate",
      itemLabelFormatter: percentLabelFormatterDecimalValue,
    },
    averageNegotiationRate: createColumnConfig(
      (item) => item.volumeAndTargetForSelected.averageNegotiationRate,
      {
        columnLabel: "Actual rate",
        itemLabelFormatter: percentLabelFormatterDecimalValue,
      }
    ),
    avgMeterPrice: {
      itemValueResolver: ({
        volumeAndTargetForSelected: { avgMeterPrice },
      }) => {
        return avgMeterPrice === null || avgMeterPrice === 0
          ? -Infinity
          : avgMeterPrice;
      },
      columnLabel: "Price/m",
      renderCell: ({
        item: {
          volumeAndTargetForSelected: { avgMeterPrice },
        },
      }) => <Txt>{currencyValue(avgMeterPrice, currencyCode)}</Txt>,
    },
    avgMeterPriceDiffInPercent: {
      itemValueResolver: ({
        volumeAndTargetForSelected: { avgMeterPriceDiffInPercent },
      }) =>
        avgMeterPriceDiffInPercent === null
          ? -Infinity
          : avgMeterPriceDiffInPercent,
      columnLabel: "Diff prev year",
      renderCell: ({
        item: {
          volumeAndTargetForSelected: { avgMeterPriceDiffInPercent },
        },
      }) => (
        <Txt>
          {percentLabelFormatterDecimalValue(avgMeterPriceDiffInPercent)}
        </Txt>
      ),
    },
  },
  columnOrder: [
    "customerName",
    "negotiationStatus",
    "customerNumber",
    "statNumber",
    "parentNumber",
    "sellerId",
    "lastYearVolume",
    "expectedVolume",
    "averageTarget",
    "averageNegotiationRate",
    "avgMeterPrice",
    "avgMeterPriceDiffInPercent",
  ],
});
